import type { EChartsOption } from 'echarts';

import { GraphContainer } from '@/components/GraphContainer';
import { Spinner } from '@/components/Spinner';
import { graphConfig } from '@/configs/Graphs';
import { BehaviorChart } from '@/features/forecasts/components/order-behaviour/BehaviorChart';
import { useAggregateSkuWape, type SkuWapeDiff } from '@/features/forecasts/hooks/useAggregateSkuWape';
import { buildTooltipSeriesLine, joinTooltipLines } from '@/features/forecasts/utils/forecastTooltip';
import { createNoDataOption } from '@/utils/graphs';

type Props = {
  chartGroup?: string;
};

const fmtPct = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
// WAPE difference rendered as signed percentage points.
const fmtPp = (v: number | null) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)} pp`);

// Legend / series names. BFG v2.2 wins where FF reduces error (diff > 0).
const DEEP_WINS = 'BFG v2.2 wins';
const INCUMBENT_WINS = 'Incumbent wins';

// Short label for a series from its primary keys (product is the most useful axis tick).
function skuLabel(row: SkuWapeDiff): string {
  const pk = row.primaryKeys;
  return String(pk.product_id ?? pk.sku ?? row.key);
}

// Aggregate per-SKU forecast performance for the 5-04 monthly run: one bar per
// series of diff = |ETS WAPE| − |BFG WAPE| (Incumbent − Foundation Forecasting),
// sorted descending. Positive (dark blue) = FF reduces error vs the incumbent;
// negative (light blue) = incumbent wins.
export function AggregateSkuWapeChart({ chartGroup }: Props) {
  const { rows, isLoading, isError } = useAggregateSkuWape();
  const { colors, grid, axisLabel } = graphConfig;

  const plotted = rows.filter((r) => r.diff != null);
  const hasData = plotted.length > 0;

  // FF reduces error where the difference is positive (lower WAPE than incumbent).
  const improved = plotted.filter((r) => (r.diff ?? 0) > 0).length;
  const improvedPct = hasData ? Math.round((improved / plotted.length) * 100) : 0;
  // Until data loads, keep the title neutral (don't claim "0% of SKUs").
  const title = hasData
    ? `Foundation Forecasting Reduces Error on ${improvedPct}% of SKUs`
    : 'Foundation Forecasting vs Incumbent — Per-SKU WAPE';

  const subtitle = hasData ? `${improved} of ${plotted.length} series` : undefined;

  // A handful of series have diffs in the tens of thousands of pp (WAPE blows up
  // when actuals are ~0), which flattens every other bar. Cap the axis: anything
  // beyond is clamped to the edge, and the top tick reads "≥ 500 PP". The tooltip
  // still shows the true value. Diffs are stored as fractions (pp ÷ 100), so
  // 500 pp == 5.0 here. The bottom is floored at −100 pp (Incumbent-wins side).
  const CAP_PP = 500;
  const FLOOR_PP = 100;
  const yMin = -FLOOR_PP / 100;
  const yMax = CAP_PP / 100;

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      /* c8 ignore start */
      formatter: (params) => {
        // Two stacked series → one of the entries for this category is null; pick
        // the one carrying the row payload.
        const items = Array.isArray(params) ? params : [params];
        const item = items.find((p) => (p?.data as { row?: SkuWapeDiff } | undefined)?.row);
        const row = (item?.data as { row?: SkuWapeDiff } | undefined)?.row;
        if (!row) return '';
        return joinTooltipLines([
          buildTooltipSeriesLine({ label: 'SKU', value: skuLabel(row) }),
          buildTooltipSeriesLine({ label: 'Incumbent WAPE', value: fmtPct(row.etsWape) }),
          buildTooltipSeriesLine({ label: 'Foundation Forecasting WAPE', value: fmtPct(row.bfgWape) }),
          buildTooltipSeriesLine({ label: 'Difference (Incumbent − FF)', value: fmtPp(row.diff) }),
          (row.diff! > yMax || row.diff! < yMin) &&
            buildTooltipSeriesLine({ label: '⚠︎', value: 'off-scale (bar clamped to axis)' }),
        ]);
      },
      /* c8 ignore stop */
    },
    legend: {
      data: [DEEP_WINS, INCUMBENT_WINS],
      top: 0,
      right: 0,
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 12,
      textStyle: { fontSize: 11 },
    },
    // Extra left/bottom room for the rotated y-axis name and the x-axis name.
    // containLabel keeps the tick labels in; the explicit left clears the
    // rotated axis name that sits outside them.
    grid: { ...grid, left: 64, right: 16, bottom: 72, top: 32, containLabel: true },
    xAxis: {
      type: 'category',
      name: '% of Revenue',
      nameLocation: 'middle',
      nameGap: 20,
      data: plotted.map(skuLabel),
      axisLabel: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'WAPE difference (Incumbent − Foundation Forecasting), percentage points',
      nameLocation: 'middle',
      nameGap: 44,
      nameTextStyle: { fontSize: 11, color: '#6b7280' },
      min: yMin,
      max: yMax,
      // Tick every 100 pp so the +500 / −100 pp edges land exactly on a labelled tick.
      interval: 1,
      // Difference is shown in percentage points (WAPE × 100). The capped edge
      // ticks read "≥ 500 PP" (top) / "≤ −100 PP" (bottom) since bars beyond are
      // clamped there.
      axisLabel: {
        ...axisLabel,
        formatter: (v: number) => {
          const pp = Math.round(v * 100);
          if (pp >= CAP_PP) return `≥ ${CAP_PP} PP`;
          if (pp <= -FLOOR_PP) return `≤ −${FLOOR_PP} PP`;
          return `${pp}`;
        },
      },
    },
    dataZoom: [{ type: 'slider', filterMode: 'none', bottom: 5, height: 20 }],
    series: [DEEP_WINS, INCUMBENT_WINS].map((seriesName, i) => {
      const isDeep = seriesName === DEEP_WINS;
      // Dark blue = Deep wins (FF reduces error), light blue = Incumbent wins.
      const baseColor = isDeep ? colors.actuals : colors.actualsLight;
      return {
        name: seriesName,
        type: 'bar' as const,
        stack: 'wape',
        barMaxWidth: 24,
        itemStyle: { color: baseColor },
        // Vertical boundary between the winning (left) and losing (right) SKUs.
        // Bars are sorted descending, so `improved` is the index of the first
        // non-winning SKU — the line sits on that category boundary.
        ...(i === 0 && improved > 0 && improved < plotted.length
          ? {
              markLine: {
                silent: true,
                symbol: 'none',
                z: 10,
                lineStyle: { type: 'dashed', color: '#4b5563', width: 1.5 },
                label: { show: false },
                data: [{ xAxis: improved }],
              },
            }
          : {}),
        // Each SKU belongs to exactly one series (the other gets null), so the
        // bars stay aligned to their category and the legend reads cleanly.
        data: plotted.map((row) => {
          const diff = row.diff as number;
          // BFG wins where diff > 0; ties (diff === 0) fall to Incumbent.
          if (isDeep ? diff <= 0 : diff > 0) return null;
          // Clamp off-scale bars to the axis edge so they pin to the top/bottom
          // instead of crushing the rest. They keep their series color (the
          // tooltip flags the true off-scale value).
          const overflow = diff > yMax || diff < yMin;
          return {
            value: overflow ? (diff > yMax ? yMax : yMin) : diff,
            row,
          };
        }),
      };
    }),
  } satisfies EChartsOption;

  // Header with the title, the "N of M series" subtitle, and a yellow WIP badge.
  const header = (
    <h3 className="text-sm font-semibold text-gray-900 pr-6">
      {title}
      {subtitle && <span className="ml-2 font-normal text-gray-500">{subtitle}</span>}
      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 align-middle">
        WIP
      </span>
    </h3>
  );

  if (isLoading) {
    return (
      <GraphContainer header={header}>
        <div className="flex items-center justify-center" style={{ height: 500 }}>
          <Spinner />
        </div>
      </GraphContainer>
    );
  }

  return (
    <BehaviorChart
      chartGroup={chartGroup}
      height={500}
      option={isError || !hasData ? createNoDataOption('No data') : chartOptions}
      title={title}
      titleInfo={subtitle}
      header={header}
    />
  );
}
